import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSeo } from "@/hooks/useSeo";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldAlert, Clock, Lock, CheckCircle2, AlertTriangle, ChevronLeft, ChevronRight, FileCheck2 } from "lucide-react";

/**
 * Native CSI exam taker.
 * Anti-cheat: blocks copy/paste/context-menu in essay fields,
 * logs tab blur + visibility change events to the backend for admin review.
 */
export default function ExamTakerPage() {
  useSeo({ title: "Examination", noindex: true });
  const { courseId } = useParams();
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const [exam, setExam] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [attempt, setAttempt] = useState(null);        // current in-progress attempt
  const [current, setCurrent] = useState(0);
  const [answers, setAnswers] = useState({});           // local mirror
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);     // the final attempt record
  const [loading, setLoading] = useState(true);
  const [remainingSec, setRemainingSec] = useState(null);
  const [showHonour, setShowHonour] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [honourName, setHonourName] = useState("");
  const [honourAgreed, setHonourAgreed] = useState(false);
  const [introRead, setIntroRead] = useState(false);
  const [leaveTarget, setLeaveTarget] = useState(null);
  const [focusWarn, setFocusWarn] = useState(false);
  const integrityLockedRef = useRef(false);

  const logIntegrity = async (type, detail = "") => {
    if (!attempt?.id || integrityLockedRef.current) return;
    try { await api.post(`/exams/attempts/${attempt.id}/integrity-event`, { type, detail }); } catch {}
  };

  // Fetch exam + prior attempts
  useEffect(() => {
    if (!user) { nav("/login"); return; }
    (async () => {
      try {
        const { data } = await api.get(`/exams/course/${courseId}`);
        setExam(data.exam);
        setAttempts(data.attempts || []);
        api.get(`/courses/${courseId}/benchmark`).then((r) => setOutcome(r.data.outcome)).catch(() => {});
        const live = (data.attempts || []).find((a) => a.status === "in_progress");
        if (live) {
          setAttempt(live);
          setAnswers(live.answers || {});
        }
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Could not load exam");
      } finally {
        setLoading(false);
      }
    })();
  }, [courseId, user, nav]);

  // Timer
  useEffect(() => {
    if (!attempt || attempt.status !== "in_progress" || !attempt.time_limit_minutes) return;
    const started = new Date(attempt.started_at).getTime();
    const deadline = started + attempt.time_limit_minutes * 60 * 1000;
    const tick = () => {
      const rem = Math.max(0, Math.round((deadline - Date.now()) / 1000));
      setRemainingSec(rem);
      if (rem === 0) handleSubmit(true);
    };
    tick();
    const h = setInterval(tick, 1000);
    return () => clearInterval(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  // Integrity listeners
  useEffect(() => {
    if (!attempt?.id || attempt.status !== "in_progress") return;
    const onBlur = () => logIntegrity("blur");
    const onVisibility = () => { if (document.hidden) { logIntegrity("visibility_hidden"); setFocusWarn(true); } };
    const onContext = (e) => { e.preventDefault(); logIntegrity("right_click_blocked"); };
    window.addEventListener("blur", onBlur);
    document.addEventListener("visibilitychange", onVisibility);
    document.addEventListener("contextmenu", onContext);
    return () => {
      window.removeEventListener("blur", onBlur);
      document.removeEventListener("visibilitychange", onVisibility);
      document.removeEventListener("contextmenu", onContext);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  // Leave guard: warn on refresh/close, in-app links, logout and browser back; submit if the candidate leaves
  useEffect(() => {
    if (!attempt?.id || attempt.status !== "in_progress") return;
    const attemptId = attempt.id;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    const onPageHide = () => {
      if (integrityLockedRef.current) return;
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/exams/attempts/${attemptId}/submit`, { method: "POST", credentials: "include", keepalive: true });
    };
    const onClick = (e) => {
      const el = e.target.closest("a[href], [data-testid='nav-logout-btn']");
      if (!el || el.closest("[data-exam-safe]")) return;
      if (el.tagName === "A" && (el.target === "_blank" || el.getAttribute("href")?.startsWith("#"))) return;
      e.preventDefault(); e.stopPropagation();
      setLeaveTarget(el.tagName === "A" ? { href: el.getAttribute("href") } : { logout: true });
    };
    const onPop = () => { window.history.pushState(null, "", window.location.href); setLeaveTarget({ back: true }); };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("popstate", onPop);
    document.addEventListener("click", onClick, true);
    window.__examLeaveGuard = () => setLeaveTarget({ logout: true });
    return () => {
      delete window.__examLeaveGuard;
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("popstate", onPop);
      document.removeEventListener("click", onClick, true);
    };
  }, [attempt]);

  const leaveAndSubmit = async () => {
    const target = leaveTarget;
    setLeaveTarget(null);
    await logIntegrity("left_exam", target?.href || (target?.logout ? "logout" : "back"));
    await handleSubmit(true, "left");
    if (target?.logout) { await logout(); nav("/"); }
    else if (target?.href) nav(target.href);
    else nav(`/courses/${courseId}`);
  };

  const startAttempt = async () => {
    try {
      const payload = honourName.trim() ? { honour_code_name: honourName.trim() } : {};
      const { data } = await api.post(`/exams/${exam.id}/attempts`, payload);
      setAttempt(data.attempt);
      setAnswers(data.attempt.answers || {});
      setCurrent(0);
      setShowHonour(false);
      toast.success(data.resumed ? "Resumed your previous attempt" : "Good luck! The timer has started.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not start the attempt");
    }
  };

  const openHonourStep = () => {
    setHonourName(user?.name || "");
    setHonourAgreed(false);
    setShowHonour(true);
    logIntegrity("intro_accepted");
  };

  const saveAnswer = async (q, value) => {
    setAnswers((prev) => ({ ...prev, [q.id]: value }));
    try {
      const body = { question_id: q.id };
      if (q.type === "essay") body.essay_text = value.essay_text || "";
      else body.selected_index = value.selected_index;
      await api.post(`/exams/attempts/${attempt.id}/answer`, body);
    } catch (e) {
      // non-blocking
    }
  };

  const handleSubmit = async (auto = false, reason = "time") => {
    if (submitting) return;
    if (!auto) {
      const unanswered = exam.questions.filter((q) => !answers[q.id]).length;
      if (unanswered > 0 && !window.confirm(`You have ${unanswered} unanswered question(s). Submit anyway?`)) return;
    }
    setSubmitting(true);
    integrityLockedRef.current = true;
    try {
      const { data } = await api.post(`/exams/attempts/${attempt.id}/submit`);
      setSubmitted(data);
      setAttempt(null);
      toast.success(auto ? (reason === "left" ? "You left the exam - your attempt has been submitted." : "Time's up - your attempt has been submitted.") : "Submitted. Awaiting grading if there are essay questions.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Submit failed");
      integrityLockedRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  const blockPaste = (e) => {
    e.preventDefault();
    toast.error("Paste is disabled inside exam answers.");
    logIntegrity("paste_blocked", `q=${exam.questions[current]?.id}`);
  };
  const blockCopy = (e) => { e.preventDefault(); logIntegrity("copy_blocked"); };

  if (loading) return <div className="min-h-[60vh] grid place-items-center text-zinc-500">Loading exam…</div>;
  if (!exam) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <h1 className="font-display text-3xl text-[var(--navy)]">No exam configured yet</h1>
        <p className="text-sm text-zinc-600 mt-2">Your instructor hasn't published the exam for this course yet. Please check back soon.</p>
        <Link to={`/courses/${courseId}`} className="chip inline-flex items-center gap-1 mt-6">← Back to course</Link>
      </div>
    );
  }

  // Submitted / review state
  if (submitted) {
    return <ExamSummary attempt={submitted} exam={exam} courseId={courseId} />;
  }

  // Prior attempts summary / start screen
  if (!attempt) {
    const taken = outcome ? outcome.attempts_used : attempts.length;
    const canStart = outcome ? outcome.can_resit : taken < (exam.max_attempts || 2);
    const passedFinal = outcome?.final_status === "passed";
    return (
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to={`/courses/${courseId}`} className="chip inline-flex items-center gap-1 mb-6">← Back to course</Link>
        <div className="eyebrow text-[var(--gold-soft)]">CSI Exam</div>
        <h1 className="font-display text-3xl sm:text-4xl text-[var(--navy)]">{exam.title}</h1>
        {exam.instructions && (
          <p className="text-sm text-zinc-700 mt-3 whitespace-pre-line" data-testid="exam-instructions">{exam.instructions}</p>
        )}
        <div className="grid sm:grid-cols-3 gap-3 mt-5 text-sm">
          <div className="card-warm p-3"><div className="eyebrow">Questions</div><div className="font-display text-xl">{exam.questions?.length || 0}</div></div>
          <div className="card-warm p-3"><div className="eyebrow">Time limit</div><div className="font-display text-xl" data-testid="exam-time-limit">{fmtLimit(exam.time_limit_minutes)}</div></div>
          <div className="card-warm p-3"><div className="eyebrow">Attempts</div><div className="font-display text-xl">{taken} / {exam.max_attempts || 2}</div></div>
        </div>
        <ExamIntroduction exam={exam} read={introRead} setRead={setIntroRead}/>
        {attempts.length > 0 && (
          <div className="mt-6">
            <div className="eyebrow mb-2">Previous attempts</div>
            <ul className="text-sm space-y-1">
              {attempts.map((a) => (
                <li key={a.id} className="flex items-center gap-3 text-zinc-700" data-testid={`prior-attempt-${a.id}`}>
                  <span>{(a.submitted_at || a.started_at || "").slice(0,16).replace("T"," ")}</span>
                  <span className="font-mono">{a.auto_score ?? 0}/{a.auto_total ?? 0} auto</span>
                  <StatusPill status={a.status}/>
                </li>
              ))}
            </ul>
          </div>
        )}
        {passedFinal && (
          <div className="mt-6 rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900" data-testid="exam-passed-final-note">
            <b>Completed · PASS (final).</b> Your certificate will be sent by the CSI Administrator by separate email within 24 hours - please also check your spam folder.
            Your results report and transcript are available on the <Link to={`/courses/${courseId}`} className="underline">course page</Link>.
            {canStart && " You may use your remaining resit to improve your grade - your pass stays final."}
          </div>
        )}
        <div className="mt-8">
          {canStart ? (
            <button onClick={openHonourStep} disabled={!introRead} title={introRead ? "" : "Please read and accept the introduction first"} className="btn-terracotta px-6 py-3 rounded-full text-sm font-semibold disabled:opacity-40" data-testid="start-exam-btn">
              {passedFinal ? "Resit to improve grade" : "Begin exam"}
            </button>
          ) : outcome?.needs_repayment ? (
            <div className="text-sm text-zinc-600" data-testid="exam-repay-note"><Lock size={14} className="inline mr-1"/> All attempts were not successful. To resit, please pay for the exam again from the <Link to={`/courses/${courseId}`} className="underline">course page</Link> - your records are kept.</div>
          ) : !passedFinal && (
            <div className="text-sm text-zinc-600"><Lock size={14} className="inline mr-1"/> You have used all {exam.max_attempts || 2} attempts. Your result will appear once grading is complete.</div>
          )}
        </div>

        {showHonour && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" data-testid="honour-code-modal">
              <div className="eyebrow text-[var(--gold-soft)] inline-flex items-center gap-1"><ShieldAlert size={13}/> Honour-code declaration</div>
              <h3 className="font-display text-2xl text-[var(--navy)] mt-1">Before you begin</h3>
              <HonourCodeStatement/>
              <div className="mt-5 space-y-3">
                <label className="block">
                  <span className="eyebrow block mb-1">Type your full legal name as signature</span>
                  <input
                    value={honourName}
                    onChange={(e) => setHonourName(e.target.value)}
                    className="w-full p-2 border border-[var(--border-warm)] rounded-lg font-mono"
                    placeholder="e.g. Jane A. Doe"
                    autoFocus
                    data-testid="honour-code-name"
                  />
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input type="checkbox" checked={honourAgreed} onChange={(e) => setHonourAgreed(e.target.checked)} className="mt-1" data-testid="honour-code-agree"/>
                  <span>I have read the CSI Honour Code, I agree to abide by it, and I confirm that my typed signature is binding.</span>
                </label>
              </div>
              <div className="flex gap-2 justify-end mt-6">
                <button onClick={() => setShowHonour(false)} className="chip" data-testid="honour-code-cancel">Cancel</button>
                <button
                  onClick={startAttempt}
                  disabled={!honourAgreed || honourName.trim().length < 2}
                  className="btn-terracotta px-5 py-2 rounded-full text-sm font-semibold disabled:opacity-40"
                  data-testid="honour-code-confirm"
                >
                  I agree - begin exam
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Live attempt
  const q = exam.questions[current];
  const total = exam.questions.length;
  const timeLabel = remainingSec == null ? null :
    `${String(Math.floor(remainingSec/60)).padStart(2,"0")}:${String(remainingSec%60).padStart(2,"0")}`;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" onCopy={blockCopy}>
      {leaveTarget && <LeaveExamModal onStay={() => setLeaveTarget(null)} onLeave={leaveAndSubmit}/>}
      {focusWarn && !leaveTarget && (
        <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 flex items-start gap-2" data-testid="exam-focus-warning">
          <AlertTriangle size={16} className="mt-0.5 shrink-0"/>
          <div className="flex-1">You left the exam window. This has been recorded for the examiner. Leaving the exam page will close and submit your exam.</div>
          <button onClick={() => setFocusWarn(false)} className="chip text-xs" data-testid="exam-focus-warning-dismiss">OK</button>
        </div>
      )}
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs text-zinc-500">{exam.title}</div>
        {timeLabel && (
          <div className={`inline-flex items-center gap-1 text-sm font-mono px-3 py-1 rounded-full ${remainingSec < 60 ? "bg-red-100 text-red-700" : "bg-[var(--muted-surface)] text-[var(--navy)]"}`} data-testid="exam-timer">
            <Clock size={14}/> {timeLabel}
          </div>
        )}
      </div>
      <div className="h-1 rounded-full bg-[var(--muted-surface)] mb-3">
        <div className="h-1 rounded-full bg-[var(--terracotta)] transition-all" style={{ width: `${((current+1)/total)*100}%` }}/>
      </div>
      <div className="text-xs text-zinc-500 mb-3">Question {current+1} of {total}</div>

      <div className="card-warm p-5" data-testid={`question-${q.id}`}>
        <h2 className="font-display text-xl text-[var(--navy)] whitespace-pre-line">{q.prompt}</h2>
        <div className="mt-5">
          {q.type === "mcq" || q.type === "tf" ? (
            <div className="space-y-2">
              {(q.options || []).map((opt, i) => {
                const active = answers[q.id]?.selected_index === i;
                return (
                  <label key={i} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${active ? "border-[var(--terracotta)] bg-[var(--sand)]" : "border-[var(--border-warm)] hover:border-[var(--terracotta)]"}`} data-testid={`option-${q.id}-${i}`}>
                    <input
                      type="radio"
                      name={q.id}
                      checked={active}
                      onChange={() => saveAnswer(q, { selected_index: i })}
                      className="mt-1"
                    />
                    <span className="text-sm text-zinc-800">{opt}</span>
                  </label>
                );
              })}
            </div>
          ) : (
            <div>
              <textarea
                value={answers[q.id]?.essay_text || ""}
                onChange={(e) => saveAnswer(q, { essay_text: e.target.value })}
                onPaste={blockPaste}
                onCopy={blockCopy}
                onCut={blockCopy}
                onDrop={(e) => { e.preventDefault(); logIntegrity("paste_blocked", "drop"); }}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                className="w-full min-h-[220px] p-3 border border-[var(--border-warm)] rounded-xl text-sm font-mono"
                placeholder="Type your answer. Paste is disabled; please write in your own words."
                data-testid={`essay-${q.id}`}
              />
              <div className="text-[11px] text-zinc-500 mt-1">
                {(answers[q.id]?.essay_text || "").trim().split(/\s+/).filter(Boolean).length} words
                {q.points ? ` · worth ${q.points} pts (admin-graded)` : ""}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mt-4">
        <button
          disabled={current === 0}
          onClick={() => setCurrent((c) => Math.max(0, c-1))}
          className="chip inline-flex items-center gap-1 text-sm disabled:opacity-30"
          data-testid="exam-prev"
        >
          <ChevronLeft size={14}/> Previous
        </button>
        {current < total - 1 ? (
          <button onClick={() => setCurrent((c) => Math.min(total-1, c+1))} className="chip inline-flex items-center gap-1 text-sm" data-testid="exam-next">
            Next <ChevronRight size={14}/>
          </button>
        ) : (
          <button
            onClick={() => handleSubmit(false)}
            disabled={submitting}
            className="btn-terracotta px-5 py-2 rounded-full text-sm font-semibold inline-flex items-center gap-2"
            data-testid="exam-submit"
          >
            <CheckCircle2 size={14}/> Submit exam
          </button>
        )}
      </div>

      <div className="mt-6 flex flex-wrap gap-1">
        {exam.questions.map((qq, i) => (
          <button
            key={qq.id}
            onClick={() => setCurrent(i)}
            className={`w-9 h-9 rounded-lg text-xs font-mono border ${i === current ? "bg-[var(--navy)] text-white border-[var(--navy)]" : answers[qq.id] ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "border-[var(--border-warm)]"}`}
            data-testid={`exam-jump-${i}`}
          >
            {i+1}
          </button>
        ))}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    in_progress: ["bg-amber-100 text-amber-800", "In progress"],
    submitted: ["bg-blue-100 text-blue-800", "Awaiting grading"],
    graded: ["bg-zinc-100 text-zinc-700", "Graded"],
    passed: ["bg-emerald-100 text-emerald-800", "Pass"],
    failed: ["bg-red-100 text-red-800", "Fail"],
  };
  const [cls, label] = map[status] || ["bg-zinc-100 text-zinc-700", status];
  return <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>{label}</span>;
}

function ExamSummary({ attempt, exam, courseId }) {
  const hasEssay = exam.questions.some((q) => q.type === "essay");
  return (
    <div className="max-w-2xl mx-auto px-4 py-16 text-center">
      <FileCheck2 size={48} className="mx-auto text-[var(--terracotta)]"/>
      <h1 className="font-display text-3xl mt-4 text-[var(--navy)]">Submitted - thank you</h1>
      <p className="text-sm text-zinc-700 mt-3 max-w-md mx-auto">
        {hasEssay
          ? "Your essay answers are now with the CSI examiner for grading. We'll email you once your result is finalised."
          : "Your attempt has been automatically graded. See your result below."}
      </p>
      <div className="card-warm p-5 mt-6 text-left max-w-md mx-auto">
        <div className="eyebrow">Auto-graded score</div>
        <div className="font-display text-3xl text-[var(--navy)]" data-testid="exam-auto-score">
          {attempt.auto_score}/{attempt.auto_total}
        </div>
        <div className="text-xs text-zinc-500 mt-1">Essay points (if any) are added by the examiner.</div>
      </div>
      <Link to={`/courses/${courseId}`} className="chip inline-flex items-center gap-1 mt-8">← Back to course</Link>
    </div>
  );
}

const fmtLimit = (m) => (!m ? "Untimed" : m % 60 === 0 ? `${m / 60} hours` : `${m} min`);

function ExamIntroduction({ exam, read, setRead }) {
  const rules = [
    `Time limit: ${fmtLimit(exam.time_limit_minutes)}. The timer starts when you begin and your exam is submitted automatically when time runs out.`,
    `Attempts: ${exam.max_attempts || 2} in total. A pass is final; a remaining attempt may be used to improve your grade.`,
    exam.pass_mark_percent != null ? `Pass mark: ${exam.pass_mark_percent}%. Essay answers are graded by a CSI examiner.` : "Essay answers are graded by a CSI examiner against the published rubric.",
    "Complete the exam in one sitting. Leaving the exam page, logging out, using the back button or closing the browser will close and submit your exam.",
    "Switching tabs or windows, copy/paste and right-click are blocked or recorded for examiner review.",
    "Your results, transcript and results report become available on your course page after grading. Certificates are not downloadable from the portal; the CSI Administrator sends them by separate email within 24 hours.",
  ];
  return (
    <div className="mt-6 card-warm p-5" data-testid="exam-introduction">
      <div className="eyebrow inline-flex items-center gap-1"><FileCheck2 size={13}/> Step 1 of 2 · Introduction</div>
      <h2 className="font-display text-2xl text-[var(--navy)] mt-1">Before you start</h2>
      <ol className="mt-3 space-y-2 text-sm text-zinc-700 list-decimal pl-5">
        {rules.map((r) => <li key={r}>{r}</li>)}
      </ol>
      <label className="mt-4 flex items-start gap-2 text-sm font-semibold cursor-pointer">
        <input type="checkbox" checked={read} onChange={(e) => setRead(e.target.checked)} className="mt-1" data-testid="intro-read-checkbox"/>
        <span>I have read and understood the introduction and examination rules.</span>
      </label>
    </div>
  );
}

const HONOUR_CODE = [
  "The answers I submit are entirely my own original work.",
  "I will not consult any other person, AI assistant, book, note, website or application during the exam, unless the exam explicitly permits it.",
  "I will not copy, record, photograph, share or distribute any exam question or answer, during or after the exam.",
  "I will complete the exam alone, in one sitting, within the 3-hour time limit.",
  "I understand that suspected breaches are reviewed by the CSI examiner and may invalidate my result, lead to disqualification and be reported under the GCSB Code of Professional Conduct.",
];

function HonourCodeStatement() {
  return (
    <div className="mt-3" data-testid="honour-code-statement">
      <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Step 2 of 2 · CSI Honour Code</div>
      <ul className="mt-2 space-y-1.5 text-sm text-zinc-700 list-disc pl-5">
        {HONOUR_CODE.map((h) => <li key={h}>{h}</li>)}
      </ul>
    </div>
  );
}

function LeaveExamModal({ onStay, onLeave }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" data-exam-safe>
      <div className="bg-white rounded-2xl max-w-md w-full p-6" data-testid="leave-exam-modal">
        <div className="inline-flex items-center gap-1 text-red-700 text-xs font-bold uppercase tracking-wider"><AlertTriangle size={14}/> Warning</div>
        <h3 className="font-display text-2xl text-[var(--navy)] mt-1">Leave the exam?</h3>
        <p className="text-sm text-zinc-700 mt-3">Leaving will <b>close and submit your exam</b> with the answers saved so far. This counts as one attempt.</p>
        <div className="flex flex-wrap gap-2 justify-end mt-6">
          <button onClick={onStay} className="btn-terracotta px-5 py-2 rounded-full text-sm font-semibold" data-testid="leave-exam-stay">Stay in exam</button>
          <button onClick={onLeave} className="chip text-sm text-red-700" data-testid="leave-exam-confirm">Leave and submit</button>
        </div>
      </div>
    </div>
  );
}
